/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 5 hook(s) need dependency review; fix each and remove this directive */
import React, { forwardRef, useId, useState, useEffect, useRef, useCallback } from "react";
import "./index.css";
import {
  SlickgridReact,
  ExtensionName,
  type PubSubService,
  type SlickgridReactInstance,
  type GridOption,
  type Column,
  type OnScrollEventArgs,
  type OnClickEventArgs,
  type SlickDataView,
  type SlickGrid as SlickGridType,
} from "slickgrid-react";
import { ReactRowDetailView } from "@slickgrid-universal/react-row-detail-plugin";
import "@slickgrid-universal/common/dist/styles/css/slickgrid-theme-material.css";
import PagerComponent from "./Pagination";
import CircularProgress from "@mui/material/CircularProgress";
import Shimmer from "../../../components/Queries/Shimmer";
import { ExcelExportService } from "@slickgrid-universal/excel-export";
import ToolBar, { ToolbarItem } from "./ToolBar";
import type { ColumnDefinition as ToolBarColumn } from "./ToolBar";
import FilterComponent from "./FilterComponent";
import type { ColumnDefinition as FilterColumn, Props as FilterComponentProps } from "./FilterComponent";
import axiosWrapper from "../../../services/axiosWrapper";
import { mountCellReact, unmountAllCellRoots } from "../cellReactRoot";
import Text from "../Text/Text";
import get from "lodash/get";
import debounce from "lodash/debounce";
import dayjs, { Dayjs } from "dayjs";
import { Box } from "@mui/material";
import { useTranslation, untranslated } from "@i18n";
import DOMPurify from "dompurify";
import type { Formatter, SlickEventData } from "@slickgrid-universal/common";
import { cloneDeep } from "lodash";

/** Internal types for SlickGrid metadata and row spans */
interface RowMetadata {
  columns?: Record<number, { rowspan: number }>;
  cssClasses?: string;
}

interface RowSpanResult<T extends GridRowBase> {
  newDataset: T[];
  metadata: Record<number, RowMetadata>;
}

/** Filter condition stored for each column */
interface FilterCondition {
  id: string;
  type: FilterType;
  option: QueryPredicate["operator"];
  text: unknown;
}

/**
 * A condition as it is actually held in `filterConditions` state. `FilterComponent.tsx` seeds an
 * entry the moment a filter popover is opened — `{ id, type, predicate }`, with no `option` and no
 * `text` until the user picks one (FilterComponent.tsx:377-397) — so `option` is absent for as long
 * as that popover is open. `getUpdatedFilterConditions` is the one place that reads the gap back and
 * fills it, which is why everything downstream of it can keep using the non-optional
 * `FilterCondition`.
 */
type StoredFilterCondition = Omit<FilterCondition, "option"> & { option?: QueryPredicate["operator"] };

/** Internal type for row move event data */
interface RowMoveData {
  insertBefore: number;
  rows: number[];
}

/** Internal type for column reorder event args */
interface ColumnsReorderedArgs {
  impactedColumns: Column[];
}

type SlickEventArgs = Record<string, unknown>;
/** Payload SlickGrid dispatches for cell click / double-click. */
type ClickEventType = CustomEvent<{ eventData: unknown; args: OnClickEventArgs }>;
type FilterType = "Text" | "Date" | "Number" | "MultiSelect";
/** Row ID type - supports string, number, or ObjectId-like objects */
type RowId = string | number | { toString(): string };
type ParentId = string | number | null;

export type GridRowBase = {
  id?: RowId;
  isRowDisabled?: boolean;
  __isDummy?: boolean;
  __parentId?: string;
  disableBorder?: boolean;
  __hasChildren?: boolean;
  __collapsed?: boolean;
  parentId?: RowId | null;
  indentLevel?: number;
  treeLevel?: number;
  hasChild?: boolean;
  isExpanded?: boolean;
  _id?: RowId;
};

export interface ColumnHeaderButton {
  cssClass?: string;
  command?: string;
  tooltip?: string;
}

export interface ColumnHeader {
  buttons?: ColumnHeaderButton[];
}

/**
 * Column definition interface for SlickGrid columns.
 * The `field` property must be a valid key of your row type.
 * The `formatter` value parameter will be typed based on the field.
 */
export interface ColumnDefinition<T extends GridRowBase, K extends keyof T = keyof T> {
  id: string | number;
  /** Field name - must be a key of the row type T */
  field: K;
  name?: string;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  sortable?: boolean;
  /** Alias for sortable */
  allowSorting?: boolean;
  filterable?: boolean;
  /** Allow filtering on this column */
  allowFiltering?: boolean;
  groupable?: boolean;
  type?: string;
  header?: ColumnHeader;
  wrapText?: boolean;
  filterType?: FilterType;
  excelExportOptions?: unknown;
  headerTemplate?: (args: { column: ColumnDefinition<T, K>; onClick: (e: unknown) => void }) => React.ReactNode;
  /** Formatter function - value type is flexible to support various column types */

  // Method shorthand, not an arrow property: under strictFunctionTypes an arrow-typed callback
  // checks its parameters contravariantly, which rejects every call site that annotates
  // `value` with the concrete cell type its own `field` implies. Method shorthand makes the
  // parameters bivariant, so those narrower formatters are accepted again.
  formatter?(row: number, cell: number, value: unknown, columnDef: unknown, dataContext: T, grid?: unknown): unknown;
  editorTemplate?: (args: { value: T[K]; updatedColumn: ColumnDefinition<T, K>; args: T }) => React.ReactNode;
  /** Custom value getter - returns computed value for sorting/filtering/display */
  _valueGetter?: (row: T) => unknown;
  hidden?: boolean;
  /** Whether the column is visible */
  visible?: boolean;
  /** Text alignment for the column */
  textAlign?: "Left" | "Right" | "Center" | string;
  /** Mark column as primary key for row identification */
  isPrimaryKey?: boolean;
}

/**
 * Brand symbol for validated column definitions.
 * This creates a "nominal" type that plain objects cannot satisfy.
 */
declare const _ValidatedColumnBrand: unique symbol;
type ValidatedColumnBrandType = typeof _ValidatedColumnBrand;

/**
 * Validated column definition type - can only be created via `typedColumns()`.
 * Plain object literals will NOT be assignable to this type.
 */
export type ValidatedColumnDefinition<T extends GridRowBase> = ColumnDefinition<T, keyof T> & {
  /** @internal Brand to ensure columns are created with typedColumns() */
  readonly __brand: ValidatedColumnBrandType;
};

/**
 * Creates validated, type-safe column definitions for SlickGrid.
 *
 * REQUIRED: All SlickGrid columns must be created with this function. What it checks, per column:
 * `field` must be a `keyof TRow`, and `editorTemplate`/`headerTemplate` are then correlated to that
 * same key - the editor receives `TRow[K]`, not a guess.
 *
 * What it does NOT check: `formatter`. Its `value` parameter is declared `unknown` for every key
 * (see `ColumnDefinition.formatter`), and it is a method shorthand, so its parameters are bivariant
 * and a formatter may annotate `value` as any type it likes without TypeScript verifying that the
 * column's own `field` produces it. Narrow `value` yourself before using it.
 *
 * @example
 * interface ResourceRow extends GridRowBase {
 *   counter: number;
 *   name: string;
 *   selectionSetIds?: string[];
 * }
 *
 * const columns = typedColumns<ResourceRow>([
 *   {
 *     id: "name",
 *     field: "name",  // ✅ TypeScript checks this is keyof ResourceRow
 *     formatter: (row, cell, value, col, data) => {
 *       // `value` arrives as `unknown` - checked here, not by the signature
 *       return typeof value === "string" ? value.toUpperCase() : "";
 *     }
 *   },
 *   {
 *     id: "selectionSetIds",
 *     field: "selectionSetIds",
 *     editorTemplate: ({ value }) => {
 *       // `value` IS correlated: `string[] | undefined`, because `field` is "selectionSetIds"
 *       return <span>{value?.join(", ") ?? ""}</span>;
 *     },
 *     formatter: (row, cell, value, col, data) => {
 *       if (!Array.isArray(value)) return "";
 *       return value.join(", ");
 *     }
 *   }
 * ]);
 */
export function typedColumns<TRow extends GridRowBase>(
  columns: Array<
    {
      [K in keyof TRow]: {
        id: string | number;
        field: K;
        name?: string;
        width?: number;
        minWidth?: number;
        maxWidth?: number;
        sortable?: boolean;
        allowSorting?: boolean;
        filterable?: boolean;
        allowFiltering?: boolean;
        groupable?: boolean;
        type?: string;
        header?: ColumnHeader;
        wrapText?: boolean;
        filterType?: FilterType;
        excelExportOptions?: unknown;
        headerTemplate?: (args: {
          column: ColumnDefinition<TRow, K>;
          onClick: (e: unknown) => void;
        }) => React.ReactNode;

        formatter?(
          row: number,
          cell: number,
          value: unknown,
          columnDef: unknown,
          dataContext: TRow,
          grid?: unknown,
        ): unknown;
        editorTemplate?: (args: {
          value: TRow[K];
          updatedColumn: ColumnDefinition<TRow, K>;
          args: TRow;
        }) => React.ReactNode;
        _valueGetter?: (row: TRow) => unknown;
        _excelValueGetter?: (row: TRow) => unknown;
        excludeFromExport?: boolean;
        hidden?: boolean;
        visible?: boolean;
        textAlign?: "Left" | "Right" | "Center" | string;
        isPrimaryKey?: boolean;
      };
    }[keyof TRow]
  >,
): ValidatedColumnDefinition<TRow>[] {
  // The parameter type above is what validates: it is distributed over `keyof TRow`, so each entry
  // must pick one key as its `field`, and `editorTemplate`/`headerTemplate` are then pinned to that
  // same `K` (`value: TRow[K]`, `column: ColumnDefinition<TRow, K>`). `formatter` is NOT part of
  // that correlation - its `value` is `unknown` for every `K` - so this mint vouches for the field
  // and editor correlation only. All the assertion itself adds is `__brand`, a `unique symbol`
  // (`_ValidatedColumnBrand`) with no runtime representation, so no value can ever carry it and no
  // cast-free expression can produce this type.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- mints the phantom `__brand` (`declare const _ValidatedColumnBrand: unique symbol`); no value can carry it, and the key-correlated parameter type above (field is a `keyof TRow`, editorTemplate/headerTemplate pinned to that same key) is the validation this mint sits behind
  return columns as ValidatedColumnDefinition<TRow>[];
}

export type QueryOperator =
  | "equal"
  | "greaterthan"
  | "greaterthanorequal"
  | "lessthan"
  | "lessthanorequal"
  | "notequal"
  | "null"
  | "notnull"
  | "startswith"
  | "endswith"
  | "contains"
  | "doesnotstartwith"
  | "doesnotendwith"
  | "doesnotcontain"
  | "like"
  | "empty"
  | "notempty"
  | "between";

export interface QueryPredicate {
  field: string;
  operator: QueryOperator;
  value: unknown;
  isComplex?: boolean;
  ignoreCase?: boolean;
  ignoreAccent?: boolean;
  /** Additional custom predicate properties */
  condition?: string;
  predicates?: QueryPredicate[];
}

export interface QuerySortInfo {
  name: string;
  direction: "ascending" | "descending";
}

export interface QueryShape {
  where?: Array<QueryPredicate | Record<string, unknown>>;
  skip?: number;
  take?: number;
  requiresCounts?: boolean;
  sorted?: QuerySortInfo[];
  nextPageToken?: string | null;
  /** Additional query parameters for backend APIs */
  select?: string[];
  expand?: string[];
  search?: string;
  /** Custom parameters passed to backend */
  params?: Record<string, unknown>;
  /** Allow additional arbitrary fields for flexibility */
  [key: string]: unknown;
}

export interface ExcelExportConfig {
  filename?: string;
  sheetName?: string;
  exportWithFormatter?: boolean;
}

// A type alias rather than an interface: the whole prop bag is forwarded verbatim to
// `SlickgridReact`, which accepts it as a plain record, and only an alias carries the implicit
// index signature that makes that hand-off checkable.
export type SlickGridProps<T extends GridRowBase> = {
  id?: string;
  showPageCount?: boolean;
  infiniteScroll?: boolean;
  showLoader?: boolean;
  disableHeader?: boolean;
  disableMarginForNoData?: boolean;
  enableWrapText?: boolean;
  enableTreeEllipsis?: boolean;
  disableRepaintOnDataChange?: boolean;
  dataset?: T[];
  datasetHierarchical?: T[];
  surpassedDataset?: T[];
  /**
   * Column definitions - MUST be created with `typedColumns<T>()` for type safety.
   *
   * @example
   * import { typedColumns } from "@CubeComponents/html/SlickGrid";
   *
   * const columns = typedColumns<MyRowType>([
   *   { id: "name", field: "name", name: "Name" },
   *   { id: "count", field: "count", name: "Count" }
   * ]);
   *
   * <SlickGrid dataset={data} columnDefinitions={columns} />
   */
  columnDefinitions: ValidatedColumnDefinition<T>[];
  query?: QueryShape;
  beforeDataBound?: (resp: unknown) => T[];
  remoteDataSource?: string;
  allowPagination?: boolean;
  allowGrouping?: boolean;
  gridOptions?: Partial<GridOption>;
  enableFiltering?: boolean;
  disableCustomSorting?: boolean;
  disableSorting?: boolean;
  enableColumnChooser?: boolean;
  enableMultiSelect?: boolean;
  onSelectedRowIdsChanged?: (args: SlickEventArgs) => void;
  disabledIds?: Array<RowId>;
  selectCheckBoxOnRowClick?: boolean;
  onGroupChanged?: (args: unknown) => void;
  onExtensionRegistered?: (ext: unknown) => void;
  setGridReady?: (ready: boolean) => void;
  onReactGridCreated?: (instance: SlickgridReactInstance) => void;
  toolbar?: ToolbarItem[];
  toolbarClickHandler?: (cmd: unknown) => void;
  showToolbarBorder?: boolean;
  callbackForVisibleColumnsUpdate?: (columns: ValidatedColumnDefinition<T>[]) => void;
  toolbarHeight?: number | string;
  propertiesPanel?: boolean;
  excelExport?: boolean;
  isEditMode?: boolean;
  handleUpdateCurrentView?: (payload: { columns: Array<string | number> }) => void;
  onClick?: (e: ClickEventType, args: SlickEventArgs) => void;
  onDblClick?: (e: { detail: { args: { row: number; cell: number } } }) => void;
  onFilterChange?: (activeKeys: string[], conditions: Record<string, FilterCondition>) => void;
  treeGrid?: boolean;
  hasChild?: boolean;
  disableRowBorder?: boolean;
  hasChildMapping?: string;
  idMapping?: string;
  parentIdMapping?: string;
  titleFormatter?: Formatter;
  initialSortColumnId?: string | number;
  dropdownColumn?: number;
  disableChildDataFetch?: boolean;
  childGridComponent?: React.ComponentType<{ row: T }> | React.FC<{ row: T }> | object;
  dragAndDrop?: boolean;
  disableTreeCollapseOnDataChange?: boolean;
  rowDrop?: (payload: { parentId: ParentId; movedRowData: T }) => Promise<boolean>;
  initialSortDirection?: "ASC" | "DESC";
  enableSelectAllCheckbox?: boolean;
  enablePointerForRows?: boolean;
  preAppliedFilters?: Record<string, FilterCondition>;
  enableGridMenu?: boolean;
  enableColumnPicker?: boolean;
  disableFullHeight?: boolean;
  onColumnsReordered?: (e: SlickEventData, args: ColumnsReorderedArgs) => void;
  renderOption?: (option: unknown) => React.ReactNode;
  /** Disable virtualisation for smaller datasets */
  disableVirtualisation?: boolean;
};

type SlickgridExtras<T extends GridRowBase> = {
  addRecord?: (rowToAdd: T, position?: number) => void;
  removeRecord?: (position?: number) => void;
  refresh?: () => void;
  resetPagination?: (skipApiCall?: boolean) => void;
  appliedFilters?: string[];
  deleteRow?: boolean;
  actual?: unknown;
};
/** Exported because TreeView.tsx types its grid handle with it (it mounts this grid). */
export type SlickgridReactInstanceX<T extends GridRowBase> = SlickgridReactInstance & SlickgridExtras<T>;

const NO_OF_FETCHED_ROWS = 1000000;
const FETCH_SIZE = 30;
const INITIAL_WRAP_RUN_DELAY_MS = 2000;
const DRAG_TOLERANCE_PX = 10;

/**
 * The operator a column's filter falls back to while the user has not picked one. Same values the
 * filter popover offers as its own defaults (FilterComponent.tsx:221-226), so a condition that is
 * read back before the user commits behaves as the popover already shows it.
 */
const DEFAULT_FILTER_OPTION: Record<FilterType, QueryPredicate["operator"]> = {
  Text: "startswith",
  Date: "equal",
  Number: "equal",
  MultiSelect: "equal",
};

function isNil(v: unknown): v is null | undefined {
  return v === null || v === undefined;
}

function toLowerString(v: unknown): string {
  if (v == null) return "";

  switch (typeof v) {
    case "string":
    case "number":
    case "boolean":
      return String(v).toLowerCase();

    case "object":
      try {
        return JSON.stringify(v).toLowerCase();
      } catch {
        return "";
      }

    default:
      return "";
  }
}

/** Date range a Date filter stores: `date` for the single-day operators, `startDate`/`endDate` for `between`. */
interface DateRangeText {
  startDate?: Dayjs;
  endDate?: Dayjs;
  date?: Dayjs;
}

/**
 * The `text` of a Date filter crosses the grid boundary untyped (`FilterCondition.text` is
 * `unknown`, and consumers fill `preAppliedFilters` themselves), so read the three date slots back
 * out one by one instead of asserting the whole shape.
 */
function toDateRangeText(value: unknown): DateRangeText {
  if (typeof value !== "object" || value === null) return {};
  return {
    startDate: "startDate" in value && dayjs.isDayjs(value.startDate) ? value.startDate : undefined,
    endDate: "endDate" in value && dayjs.isDayjs(value.endDate) ? value.endDate : undefined,
    date: "date" in value && dayjs.isDayjs(value.date) ? value.date : undefined,
  };
}

/**
 * The value a Date filter compares against is equally untyped: the picker stores a Dayjs, a
 * `{ date }` wrapper, or an already formatted string. Anything dayjs cannot parse becomes an
 * Invalid Date, which is how every comparison in `matchDateFilter` already treats an unusable
 * filter value. Dayjs is tested first because `Dayjs.prototype.date` is a method, so a raw Dayjs
 * also answers `"date" in value`.
 */
function toFilterDayjs(value: unknown): Dayjs {
  if (dayjs.isDayjs(value)) return value;
  if (typeof value === "object" && value !== null && "date" in value) return toFilterDayjs(value.date);
  const input: dayjs.ConfigType =
    typeof value === "string" || typeof value === "number" || value instanceof Date || value == null ? value : NaN;
  return dayjs(input);
}

/**
 * The filter type carried by a live grid column. SlickGrid copies the column definitions into its
 * own vendor `Column`, which has no `filterType` of its own, so the value is read reflectively off
 * the column the grid hands back and checked against the four names this file understands.
 */
function toFilterType(column: object): FilterType | undefined {
  const value: unknown = Reflect.get(column, "filterType");
  if (value === "Text" || value === "Date" || value === "Number" || value === "MultiSelect") return value;
  return undefined;
}

/** A row id is a string, a number, or an ObjectId-like object; anything else cannot identify a row. */
function toRowId(value: unknown): RowId | undefined {
  if (typeof value === "string" || typeof value === "number") return value;
  if (typeof value === "object" && value !== null) return value;
  return undefined;
}

type DebouncedFunction<TArgs extends unknown[]> = ((...args: TArgs) => void) & { cancel: () => void };

function makeDebounce<TArgs extends unknown[]>(fn: (...args: TArgs) => void, delay: number): DebouncedFunction<TArgs> {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  const debounced = (...args: TArgs) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
  debounced.cancel = () => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = null;
  };
  return debounced;
}

function getRowElementByIndex(index: number): Element | null {
  const odd = document.querySelectorAll(".slick-row.odd");
  const even = document.querySelectorAll(".slick-row.even");
  return index % 2 === 0 ? (even[index / 2] ?? null) : (odd[Math.floor(index / 2)] ?? null);
}

function isMidpointHit(dragTop: number, rowEl: Element | null): boolean {
  if (!rowEl) return false;
  const rect = rowEl.getBoundingClientRect();
  const midY = rect.top + rect.height / 2;
  return dragTop >= midY - DRAG_TOLERANCE_PX && dragTop <= midY + DRAG_TOLERANCE_PX;
}

function getItemsSourceSafe<T extends GridRowBase>(dv: SlickDataView<T> | null | undefined): T[] {
  if (!dv) return [];
  if (typeof dv.getFilteredItems === "function") return dv.getFilteredItems();
  if (typeof dv.getItems === "function") return dv.getItems();
  return [];
}

function getDroppedRowIndex(insertBefore: number): number | null {
  const idx = insertBefore - 1;
  return idx >= 0 ? idx : null;
}

function getRowElementsSafe(movedIdx: number, droppedIdx: number) {
  const movedEl = getRowElementByIndex(movedIdx);
  const droppedEl = getRowElementByIndex(droppedIdx);
  if (!movedEl || !droppedEl) return null;
  return { movedEl, droppedEl };
}

interface HideGuideOptions<T extends GridRowBase> {
  movedElTop: number;
  rowRectTop: number;
  droppedRowData: T | null;
  movedRowData: T;
  currentTop: number;
  droppedEl: Element;
  insertBefore: number | null;
}

function shouldHideGuide<T extends GridRowBase>(opts: HideGuideOptions<T>): boolean {
  const { movedElTop, rowRectTop, droppedRowData, movedRowData, currentTop, droppedEl, insertBefore } = opts;
  if (movedElTop < rowRectTop) return true;
  if (!droppedRowData) return true;
  const differentParent = droppedRowData.parentId !== movedRowData.parentId;
  const midpointHit = isMidpointHit(currentTop, droppedEl);
  if (differentParent && !midpointHit && insertBefore !== 0) return true;
  return false;
}

function setGuide(reorderGuide: HTMLElement, show: boolean, highlight: boolean) {
  reorderGuide.style.display = show ? "block" : "none";
  if (!show) {
    reorderGuide.classList.remove("parent-child-move");
    return;
  }
  if (highlight) reorderGuide.classList.add("parent-child-move");
  else reorderGuide.classList.remove("parent-child-move");
}

// SlickGrid's own special columns (checkbox selector, row-move handle) are vendor `Column`s and are
// handed straight back to the grid, so they travel as what they are rather than as a
// `ColumnDefinition`: their `field` is a grid path rather than a key of the row type, and their
// `name` can be an element (the select-all checkbox) rather than a string. `groupable` is this
// file's own flag rather than one of the vendor's, so it is declared alongside `Column<T>`.
const mapSpecialColumn = <T extends GridRowBase>(c: Column<T>): Column<T> & { groupable: boolean } => {
  return {
    ...c,
    sortable: false,
    filterable: false,
    groupable: false,
    reorderable: false,
    selectable: false,
  };
};

function createMainRow<T extends GridRowBase>(item: T, idxCompressed: number, requiredRows: number): T {
  const rowId = item.id ?? `row_${idxCompressed}`;
  return {
    ...item,
    id: rowId,
    disableBorder: requiredRows > 1,
  };
}

function createDummyRows<T extends GridRowBase>(item: T, mainRowId: RowId, requiredRows: number): T[] {
  const dummies: T[] = [];
  for (let iIdx = 1; iIdx < requiredRows; iIdx++) {
    dummies.push({
      ...item,
      __isDummy: true,
      __parentId: String(mainRowId),
      id: `${mainRowId}_dummy_${iIdx}_${crypto.randomUUID()}`,
    } as T);
  }
  return dummies;
}

function buildMetadataForRow(columnCount: number, requiredRows: number): RowMetadata {
  const columnsMeta: Record<number, { rowspan: number }> = {};
  for (let colIdx = 0; colIdx < columnCount; colIdx++) {
    columnsMeta[colIdx] = { rowspan: requiredRows };
  }
  return { columns: columnsMeta };
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- `forwardRef` erases the render function's own generic: its result is `ForwardRefExoticComponent<SlickGridProps<GridRowBase> & ...>` (T collapsed to its default), which no annotation can widen back to a generic component - re-stating the signature is the only way to keep `T` visible to callers
const SlickGridInner = forwardRef(function SlickGridInner<T extends GridRowBase = GridRowBase>(
  props: SlickGridProps<T>,
  ref: React.ForwardedRef<SlickgridReactInstanceX<T>>,
) {
  const { t } = useTranslation();
  const { showPageCount = true } = props;

  const generatedId = useId();
  const id = props.id || generatedId;
  const standByRef = useRef<SlickgridReactInstanceX<T> | null>(null);

  // A `ForwardedRef` is null when the caller passed no ref, a callback ref, or a ref object. Only
  // the object form can be read back, so the other two fall back to the internal ref.
  const gridRef = typeof ref === "object" && ref !== null ? ref : standByRef;

  const excelExportService = React.useMemo(() => new ExcelExportService(), []);
  useEffect(() => {
    (globalThis as typeof globalThis & { excelExportService?: ExcelExportService }).excelExportService =
      excelExportService;
  }, [excelExportService]);

  const [appliedFilters, setAppliedFilters] = useState<string[]>([]);
  const [filterConditions, setFilterConditions] = useState<Record<string, FilterCondition>>({});
  const [loaderOnPageChange, setLoaderOnPageChange] = useState<boolean>(!!props.showLoader);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalItemCount, setTotalItemCount] = useState<number>(1);
  const [allPrerequisiteLoaded, setAllPrerequisiteLoaded] = useState<boolean>(false);
  const [cssClasses, setCssClasses] = useState<string>("");

  const isEditModeRef = useRef<boolean>(false);
  const nextPageTokenRef = useRef<string | null>(null);
  const isLastRef = useRef<boolean>(false);

  const [loaderForInfiniteScroll, setLoaderForInfiniteScroll] = useState<boolean>(false);
  const [currentLoadedCount, setCurrentLoadedCount] = useState<number>(0);

  useEffect(() => {
    isEditModeRef.current = !!props.isEditMode;
  }, [props.isEditMode]);

  const [totalPages, setTotalPages] = useState<number>(
    Math.ceil(totalItemCount / pageSize) === 0 ? 1 : Math.ceil(totalItemCount / pageSize),
  );
  useEffect(() => {
    setTotalPages(Math.max(1, Math.ceil(totalItemCount / pageSize)));
  }, [pageSize, totalItemCount]);
  useEffect(() => {
    setCurrentPage(1);
  }, [totalPages]);

  const [dataset, setDataset] = useState<T[]>([]);
  // The grid's own special columns ride along with the caller's definitions (see `mapSpecialColumn`).
  const [updatedColumnDefinitions, setUpdatedColumnDefinitions] = useState<Array<ColumnDefinition<T> | Column<T>>>([]);
  const [sortingDetails, setSortingDetails] = useState<false | { columnId: string; sortAsc: boolean }>(false);

  const [currentColumnId, setCurrentColumnId] = useState<string>("");
  const [filterPredicates, setFilterPredicates] = useState<Record<string, unknown> | string>("");
  const [anchorEl, setAnchorEl] = useState<{ top: number; left: number } | null>(null);
  const [filterType, setFilterType] = useState<FilterType | "">("");
  const [filterOpened, setFilterOpened] = useState<boolean>(false);
  const [excelExportExtension, setExcelExportExtension] = useState<ExcelExportService | null>(null);

  const topOfDragRowRef = useRef<number | undefined>(undefined);
  const [insertBefore, setInsertBefore] = useState<number | null>(null);
  const [dataRow, setDataRow] = useState<number | null>(null);
  const [top, setTop] = useState<number | null>(null);

  interface GridStateSnapshot<TRow extends GridRowBase> {
    pageSize: number;
    currentPage: number;
    query: QueryShape | undefined;
    originalDataset: TRow[] | undefined;
    sortingDetails: false | { columnId: string; sortAsc: boolean };
    appliedFilters: string[];
    dataset: TRow[];
    filterConditions: Record<string, FilterCondition>;
    filterPredicates: Record<string, unknown> | string;
    currentColumnId: string;
    totalItemCount: number;
  }
  const stateRef = useRef<GridStateSnapshot<T> | undefined>(undefined);
  const [gridMounted, setGridMounted] = useState<boolean>(false);

  const specialColumnRef = useRef<Column<T>[]>([]);
  const [selectedItems, setSelectedItems] = useState<number>(0);

  useEffect(() => {
    const cls: string[] = [];
    if (props.disableHeader) cls.push("disable-header");
    if (props.disableMarginForNoData) cls.push("disable-margin-no-data");
    if (props.enableSelectAllCheckbox && dataset.length > 0) cls.push("enable-selectall-checkbox");
    if (props.enableWrapText) cls.push("enable-wrap-text");
    if (props.enableTreeEllipsis) cls.push("tree-row-ellipsis");
    if (props.disableRowBorder) cls.push("disable-row-border");
    if (props.enablePointerForRows) cls.push("cursor-pointer-row");
    if (props.disableFullHeight) cls.push("disable-full-height");
    setCssClasses(cls.join(" "));
  }, [
    props.disableHeader,
    props.disableMarginForNoData,
    props.enableSelectAllCheckbox,
    props.enableWrapText,
    props.enableTreeEllipsis,
    props.disableRowBorder,
    props.enablePointerForRows,
    dataset.length,
    props.disableFullHeight,
  ]);

  useEffect(() => {
    if (props.columnDefinitions.length) {
      setAllPrerequisiteLoaded(true);
    }
  }, [dataset, props.columnDefinitions]);

  useEffect(() => {
    if (props.infiniteScroll) {
      setCurrentLoadedCount(dataset.length);
    }
  }, [dataset.length, props.infiniteScroll]);

  useEffect(() => {
    stateRef.current = {
      pageSize,
      currentPage,
      query: props.query,
      originalDataset: props.dataset,
      sortingDetails,
      appliedFilters,
      dataset,
      filterConditions,
      filterPredicates,
      currentColumnId,
      totalItemCount,
    };
  });

  useEffect(() => {
    return () => {
      gridRef.current = null;
    };
  }, [gridRef]);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      (globalThis as typeof globalThis & { gridRef1?: unknown }).gridRef1 = gridRef;
      return () => {
        (globalThis as typeof globalThis & { gridRef1?: unknown }).gridRef1 = undefined;
      };
    }
    return () => undefined;
  }, [gridRef]);

  useEffect(() => {
    const current = gridRef.current;
    if (current) {
      current.addRecord = (rowToAdd: T, position?: number) => {
        const currentItems: T[] = [...current.dataView.getItems()];
        const finalPosition =
          position === undefined || position < 0 || position > currentItems.length ? currentItems.length : position;

        const updatedItems = [...currentItems.slice(0, finalPosition), rowToAdd, ...currentItems.slice(finalPosition)];
        setDataset(updatedItems);

        setTimeout(() => {
          current.slickGrid.setSelectedRows([finalPosition]);
        }, 0);
      };

      current.removeRecord = (position?: number) => {
        if (position === undefined) return;
        const updatedItems: T[] = cloneDeep(current.dataView.getItems());
        if (position >= 0 && position < updatedItems.length) {
          updatedItems.splice(position, 1);
          setDataset(updatedItems);
        }
      };

      current.refresh = () => {
        current.slickGrid.setSelectedRows([]);
        handleDataLoad();
      };

      current.resetPagination = (skipApiCall = false) => {
        nextPageTokenRef.current = null;
        isLastRef.current = false;
        setCurrentPage(1);
        if (!skipApiCall) {
          handleDataLoad();
        }
      };
    }
  });

  const ellipseFormatterWithTooltip = (args: { value: unknown; column: ColumnDefinition<T> }) => {
    const cellValue = args.value ?? "";
    const container = document.createElement("div");
    container.style.width = "100%";
    container.style.overflow = "hidden";
    return mountCellReact(<Text tooltipContent={untranslated(String(cellValue))}>{String(cellValue)}</Text>, container);
  };

  function buildBaseFormatter(column: ColumnDefinition<T>, isEditMode: boolean) {
    if (!column.formatter && !(isEditMode && column.editorTemplate)) {
      return (_row: number, _cell: number, _value: unknown, _columnDef: unknown, dataContext: T) => {
        if (dataContext.__isDummy) return "";
        const rawValue = column._valueGetter
          ? column._valueGetter(dataContext)
          : get(dataContext, String(column.field), "");
        const cls = (column as ColumnDefinition<T> & { spanClass?: string }).spanClass || "sg-cell";
        if (props.enableWrapText) {
          return `<span class="${cls}" title="${rawValue}">${rawValue}</span>`;
        } else {
          return ellipseFormatterWithTooltip({
            value: rawValue,
            column,
          });
        }
      };
    }

    if (column.formatter && !(isEditMode && column.editorTemplate)) {
      const self = column.formatter;
      return (row: number, cell: number, value: unknown, columnDef: unknown, dataContext: T, grid?: unknown) => {
        if (dataContext.__isDummy) return "";
        if (typeof self === "function") {
          return self(row, cell, value, columnDef, dataContext, grid);
        } else {
          console.log("Formatter is not a function");
          return "";
        }
      };
    }

    return undefined;
  }
  const metadataRef = useRef<Record<number, RowMetadata>>({});

  function getRenderedLines(
    slickGrid: SlickGridType,
    dataView: SlickDataView<T>,
    itemId: RowId | undefined,
    colIndex: number,
    rowHeight: number,
  ): number {
    if (itemId == null) return 1;
    const renderedRowIndex = dataView.getRowById(String(itemId));
    if (renderedRowIndex == null || renderedRowIndex < 0) return 1;

    const cell = slickGrid.getCellNode(renderedRowIndex, colIndex);
    const firstChild = cell?.firstChild;
    const requiredHeight = firstChild instanceof HTMLElement ? firstChild.clientHeight : 0;
    return requiredHeight ? Math.ceil(requiredHeight / rowHeight) : 1;
  }

  function calculateMaxLinesForItem(
    item: T,
    columnDefs: Column<T>[],
    slickGrid: SlickGridType,
    dataView: SlickDataView<T>,
    rowHeight: number,
  ): number {
    let maxLines = 1;
    for (const [colIndex] of columnDefs.entries()) {
      const totalLines = getRenderedLines(slickGrid, dataView, item.id, colIndex, rowHeight);
      maxLines = Math.max(maxLines, totalLines);
    }
    return maxLines;
  }

  function calculateRowSpans(
    dataArr: T[],
    columnDefs: Column<T>[],
    slickGrid: SlickGridType,
    dataView: SlickDataView<T>,
    rowHeight: number,
  ): RowSpanResult<T> {
    const metadata: Record<number, RowMetadata> = {};
    const newDataset: T[] = [];

    for (const [idxCompressed, item] of dataArr.entries()) {
      if (item.__isDummy) continue;

      const maxRows = calculateMaxLinesForItem(item, columnDefs, slickGrid, dataView, rowHeight);
      const mainRow = createMainRow(item, idxCompressed, maxRows);
      const mainRowIndex = newDataset.length;

      newDataset.push(mainRow);

      if (maxRows > 1) {
        metadata[mainRowIndex] = buildMetadataForRow(columnDefs.length, maxRows);
        const dummyRows = createDummyRows(item, mainRow.id!, maxRows);
        newDataset.push(...dummyRows);
      }
    }

    return { newDataset, metadata };
  }

  const onScroll = debounce(() => {
    const current = gridRef.current;
    if (!current) return;

    const slickGrid = current.slickGrid;
    const dataView = current.dataView;
    const allItems = dataView.getItems();

    const visibleRows = slickGrid.getRenderedRange();
    const endRow = Math.min(visibleRows.bottom + 1, allItems.length);
    const visibleData = allItems.slice(visibleRows.top, endRow);

    const rowHeight = slickGrid.getOptions().rowHeight || 48;
    const columns = slickGrid.getColumns();

    const { newDataset: processedVisibleItems, metadata: processedLocalMetadata } = calculateRowSpans(
      visibleData,
      columns,
      slickGrid,
      dataView,
      rowHeight,
    );

    const newFullDataset = [...allItems.slice(0, visibleRows.top), ...processedVisibleItems, ...allItems.slice(endRow)];

    const newFullMetadata = { ...metadataRef.current };

    for (let i = visibleRows.top; i < endRow; i++) {
      delete newFullMetadata[i];
    }

    for (const localIndex in processedLocalMetadata) {
      const globalIndex = Number(localIndex) + visibleRows.top;
      const meta = processedLocalMetadata[localIndex];
      if (meta) {
        newFullMetadata[globalIndex] = meta;
      }
    }

    metadataRef.current = newFullMetadata;

    requestAnimationFrame(() => {
      dataView.beginUpdate();
      dataView.setItems(newFullDataset);
      dataView.endUpdate();

      dataView.getItemMetadata = (rowIndex: number) => {
        const item = dataView.getItem(rowIndex);
        if (!item) return null;

        const cssClasses: string[] = [];
        if (item.__isDummy) cssClasses.push("dummy-row");
        if (item.disableBorder) cssClasses.push("disable-border");

        return {
          ...metadataRef.current[rowIndex],
          cssClasses: cssClasses.join(" "),
        };
      };

      slickGrid.invalidate();
      slickGrid.render();
    });

    setTimeout(() => {
      if (props.treeGrid) {
        gridRef.current?.treeDataService.toggleTreeDataCollapse(false);
      }
    }, 500);
  }, 200);

  const handleWrapUpdate = () => {
    const current = gridRef.current;
    if (!current) return;

    const slickGrid = current.slickGrid;
    const dataView = current.dataView;
    const columns = slickGrid.getColumns();
    const rawItems = dataView.getItems().filter((it) => !it.__isDummy);

    const rowHeight = slickGrid.getOptions().rowHeight || 48;
    const { newDataset, metadata } = calculateRowSpans(rawItems, columns, slickGrid, dataView, rowHeight);

    metadataRef.current = metadata;

    current.slickGrid.remapAllColumnsRowSpan();

    requestAnimationFrame(() => {
      dataView.beginUpdate();
      dataView.setItems(newDataset);
      dataView.endUpdate();

      dataView.getItemMetadata = (rowIndex: number) => {
        const item = dataView.getItem(rowIndex);
        if (!item) return null;

        const cssClasses: string[] = [];
        if (item.__isDummy) cssClasses.push("dummy-row");
        if (item.disableBorder) cssClasses.push("disable-border");

        return {
          ...metadataRef.current[rowIndex],
          cssClasses: cssClasses.join(" "),
        };
      };

      slickGrid.invalidate();
      slickGrid.render();
    });

    setTimeout(() => {
      if (props.treeGrid) {
        gridRef.current?.treeDataService.toggleTreeDataCollapse(false);
      }
    }, 500);
  };

  useEffect(() => {
    if (!props.enableWrapText) return;
    const sg = gridRef.current?.slickGrid;
    const dataView = gridRef.current?.dataView;
    if (!sg || !dataView) return;
    const originalFilter = dataView.getFilter();
    dataView.setFilter((item: T, args?: unknown) => {
      if (item.__isDummy) return true;
      return originalFilter ? originalFilter(item, args) : true;
    });

    handleWrapUpdate();
    const onResize = makeDebounce(handleWrapUpdate, 150);
    const timer = setTimeout(() => handleWrapUpdate(), 2000);

    sg.onColumnsResized.subscribe(onResize);
    sg.onViewportChanged.subscribe(onScroll);
    return () => {
      clearTimeout(timer);
      sg.onColumnsResized.unsubscribe(onResize);
      sg.onViewportChanged.unsubscribe(onScroll);
    };
  }, [props.enableWrapText, dataset]);

  useEffect(() => {
    if (!props.disableRepaintOnDataChange && gridRef.current) {
      gridRef.current.slickGrid.invalidate();
    }
  }, [dataset, gridRef, props.disableRepaintOnDataChange]);

  const matchDateFilter = (fieldValue: unknown, filterOption: QueryOperator, filterValue: unknown): boolean => {
    const format = "MMM D,YYYY [at] hh:mm:ss A";
    const itemDate = Number.isNaN(Number(fieldValue)) ? dayjs(String(fieldValue), format) : dayjs(Number(fieldValue));
    const filterDate = toFilterDayjs(filterValue);

    switch (filterOption) {
      case "equal":
        return itemDate.isSame(filterDate, "day");
      case "greaterthan":
        return itemDate.isAfter(filterDate, "day");
      case "greaterthanorequal":
        return itemDate.isSame(filterDate, "day") || itemDate.isAfter(filterDate, "day");
      case "lessthan":
        return itemDate.isBefore(filterDate, "day");
      case "lessthanorequal":
        return itemDate.isSame(filterDate, "day") || itemDate.isBefore(filterDate, "day");
      case "notequal":
        return !itemDate.isSame(filterDate, "day");
      case "null":
        return !fieldValue || String(fieldValue).trim() === "";
      case "notnull":
        return !!fieldValue && String(fieldValue).trim() !== "";
      default:
        return false;
    }
  };

  const matchTextFilter = (fieldValue: unknown, filterOption: QueryOperator, filterValue: unknown): boolean => {
    const fieldValueStr = toLowerString(fieldValue);
    const filterValueLower = toLowerString(filterValue);

    switch (filterOption) {
      case "startswith":
        return fieldValueStr.startsWith(filterValueLower);
      case "endswith":
        return fieldValueStr.endsWith(filterValueLower);
      case "empty":
        return fieldValueStr === "";
      case "equal":
        return fieldValueStr === filterValueLower;
      case "contains":
      case "like":
        return fieldValueStr.includes(filterValueLower);
      case "doesnotstartwith":
        return !fieldValueStr.startsWith(filterValueLower);
      case "doesnotendwith":
        return !fieldValueStr.endsWith(filterValueLower);
      case "doesnotcontain":
        return !fieldValueStr.includes(filterValueLower);
      case "notequal":
        return fieldValueStr !== filterValueLower;
      case "notnull":
      case "notempty":
        return fieldValueStr !== "";
      default:
        return false;
    }
  };

  const matchNumberFilter = (fieldValue: unknown, filterOption: QueryOperator, filterValue: unknown): boolean => {
    const n = Number(fieldValue);
    const f = Number(filterValue);
    switch (filterOption) {
      case "equal":
        return n === f;
      case "greaterthan":
        return n > f;
      case "greaterthanorequal":
        return n >= f;
      case "lessthan":
        return n < f;
      case "lessthanorequal":
        return n <= f;
      case "notequal":
        return n !== f;
      case "null":
        return Number.isNaN(n);
      case "notnull":
        return !Number.isNaN(n);
      default:
        return false;
    }
  };

  const matchMultiSelectFilter = (fieldValue: unknown, filterValue: unknown): boolean => {
    const selectedValues: unknown[] = Array.isArray(filterValue) ? filterValue : [filterValue];
    if (Array.isArray(fieldValue)) {
      // Options are whatever the row held: a plain value, or an object the caller names by `name`.
      const names = fieldValue.map((it: unknown) =>
        typeof it === "object" && it !== null && "name" in it ? it.name : String(it),
      );
      return selectedValues.some((sv) => names.some((fv) => toLowerString(fv) === toLowerString(sv)));
    }
    return selectedValues.some((sv) => toLowerString(fieldValue) === toLowerString(sv));
  };

  function applyFilterLocal(
    dataArr: T[],
    columnId: string,
    filterOption: QueryOperator,
    filterValue: unknown,
    fType: FilterType,
  ): T[] {
    return dataArr.filter((item) => {
      const fieldValue = get(item, columnId) ?? "";

      switch (fType) {
        case "Date":
          return matchDateFilter(fieldValue, filterOption, filterValue);
        case "Text":
          return matchTextFilter(fieldValue, filterOption, filterValue);
        case "Number":
          return matchNumberFilter(fieldValue, filterOption, filterValue);
        case "MultiSelect":
          return matchMultiSelectFilter(fieldValue, filterValue);
        default:
          return false;
      }
    });
  }

  const getUpdatedFilterConditions = (
    conds: Record<string, StoredFilterCondition>,
  ): Record<string, FilterCondition> => {
    const updated = Object.keys(conds).reduce<Record<string, FilterCondition>>((result, filterKey) => {
      const filterValue = conds[filterKey]!;
      if (filterValue.option === "between" && filterValue.type === "Date") {
        const { text, ...otherFields } = filterValue;
        const { startDate, endDate } = toDateRangeText(text);
        if (startDate) {
          result[`${filterKey}_start`] = {
            ...otherFields,
            option: "greaterthanorequal",
            text: startDate.format(),
          };
        }
        if (endDate) {
          result[`${filterKey}_end`] = {
            ...otherFields,
            option: "lessthanorequal",
            text: endDate.format(),
          };
        }
      } else if (filterValue.type === "Date" && (filterValue.option === "equal" || !filterValue.option)) {
        const { text, ...otherFields } = filterValue;
        const dateText = toDateRangeText(text);
        const dayjsObject = dayjs();
        const startDate = (dateText.date ?? dayjsObject).startOf("day").format();
        const endDate = (dateText.date ?? dayjsObject).endOf("day").format();
        result[`${filterKey}_start`] = { ...otherFields, option: "greaterthanorequal", text: startDate };
        result[`${filterKey}_end`] = { ...otherFields, option: "lessthanorequal", text: endDate };
      } else {
        // A condition seeded by an open filter popover has no `option` yet; fill in the column
        // type's default so every reader downstream gets the operator the popover is showing.
        result[filterKey] = { ...filterValue, option: filterValue.option ?? DEFAULT_FILTER_OPTION[filterValue.type] };
      }
      return result;
    }, {});
    return updated;
  };

  useEffect(() => {
    const grid = gridRef.current?.slickGrid;
    if (!grid) return;

    const renderHeaderTemplate = (_e: unknown, args: { column: Column<T>; node: HTMLElement }) => {
      // SlickGrid hands back its own `Column`, which cannot carry this file's `headerTemplate`, so
      // look the template up on the column definition the caller supplied.
      const col = props.columnDefinitions.find((c) => c.id === args.column.id);
      if (!col?.headerTemplate) return;

      args.node.innerHTML = "";
      const mount = document.createElement("div");
      args.node.appendChild(mount);
      mountCellReact(
        col.headerTemplate({
          column: col,
          onClick: (_ev: unknown) => {},
        }),
        mount,
      );
    };

    grid.onHeaderCellRendered.subscribe(renderHeaderTemplate);
    grid.setColumns(grid.getColumns());
    grid.invalidate();
    grid.render();
    return () => {
      grid.onHeaderCellRendered.unsubscribe(renderHeaderTemplate);
    };
  }, [props.columnDefinitions, gridRef]);

  const ensureIds = React.useCallback(
    (arr: T[]): T[] =>
      arr.map((data) => {
        const out: T = { ...data };

        // Rows arrive from a remote source, so `id` can be missing at runtime whatever the row type says.
        if (out.id == null) {
          const mappedId = props.idMapping ? toRowId((out as Record<string, unknown>)[props.idMapping]) : undefined;
          const altId = out._id;

          if (mappedId) {
            out.id = mappedId;
          } else if (altId == null) {
            out.id = `row_${crypto.randomUUID()}`;
          } else {
            out.id = altId;
          }
        }
        if (props.idMapping) {
          const mappedId = toRowId((out as Record<string, unknown>)[props.idMapping]);
          if (mappedId) out.id = mappedId;
        }
        return out;
      }),
    [props.idMapping],
  );

  const mapTreeGridFlags = React.useCallback(
    (arr: T[]): T[] =>
      props.treeGrid
        ? arr.map((d) => {
            const hasChild = props.hasChildMapping
              ? Boolean((d as Record<string, unknown>)[props.hasChildMapping])
              : false;
            return {
              ...d,
              __hasChildren: hasChild,
              __collapsed: hasChild ? true : undefined,
            } as T;
          })
        : arr,
    [props.treeGrid, props.hasChildMapping],
  );

  const buildModifiedQuery = (skip: number, take: number): QueryShape => {
    const modifiedQuery: QueryShape = { ...props.query, skip, take, requiresCounts: true };

    const hasPredicates =
      filterPredicates && typeof filterPredicates === "object" && Object.keys(filterPredicates).length > 0;

    if (hasPredicates) {
      if (props.query?.where?.length) {
        modifiedQuery.where = [...props.query.where, filterPredicates];
      } else {
        modifiedQuery.where = [filterPredicates];
      }
    }
    if (sortingDetails) {
      modifiedQuery.sorted = [
        { name: sortingDetails.columnId, direction: sortingDetails.sortAsc ? "ascending" : "descending" },
      ];
    }
    if (nextPageTokenRef.current) {
      modifiedQuery.nextPageToken = nextPageTokenRef.current;
    }
    return modifiedQuery;
  };

  interface RemoteDataResponse {
    data?: {
      result?: T[];
      nextPageToken?: string;
      isLast?: boolean;
      count?: number;
    };
    result?: T[];
    nextPageToken?: string;
    isLast?: boolean;
    count?: number;
  }

  const processRemoteResponse = (response: { data?: RemoteDataResponse }): T[] => {
    const current = gridRef.current;
    if (current) current.actual = response;

    let modifiedResponse: T[];
    if (props.beforeDataBound) {
      modifiedResponse = props.beforeDataBound(response);
    } else {
      modifiedResponse = response.data?.result ?? [];
    }
    if (response.data?.nextPageToken) nextPageTokenRef.current = response.data.nextPageToken;
    if (!isNil(response.data?.isLast)) isLastRef.current = Boolean(response.data.isLast);
    if (!isNil(response.data?.count)) setTotalItemCount(Number(response.data.count));
    return modifiedResponse;
  };

  const processLocalDataset = (skip: number, take: number): T[] => {
    const updatedFilterConds = getUpdatedFilterConditions(filterConditions);

    const applyFiltersToData = (arr: T[]): T[] => {
      if (!appliedFilters.length) return arr;
      // `getUpdatedFilterConditions` has already filled in the default operator for any condition a
      // still-open filter popover seeded without one, so `cond.option` is real here.
      return Object.values(updatedFilterConds).reduce(
        (currentData: T[], cond) => applyFilterLocal(currentData, cond.id, cond.option, cond.text, cond.type),
        arr,
      );
    };

    const includeMissingParentsForTree = (filteredData: T[], items: T[]): T[] => {
      if (!props.treeGrid) return filteredData;
      const parentKey = props.parentIdMapping;
      const idMap = new Map<RowId, T>(items.map((it) => [it.id!, it]));
      const neededIds = new Set<RowId>(filteredData.map((d) => d.id!));

      const buildParentChain = (child: T): T[] => {
        const chain: T[] = [];
        let current: T = child;
        for (;;) {
          // `parentIdMapping` names the parent field at runtime, so it can only be read as a plain key.
          const rawParent = parentKey === undefined ? undefined : (current as Record<string, unknown>)[parentKey];
          if (!rawParent) break;
          const parentId = toRowId(rawParent);
          if (parentId === undefined || neededIds.has(parentId)) break;
          const parent = idMap.get(parentId);
          if (!parent) break;
          neededIds.add(parentId);
          chain.push(parent);
          current = parent;
        }
        return chain;
      };

      const appendUnique = (target: T[], seenSet: Set<RowId>, toAppend: T[]) => {
        for (let i = toAppend.length - 1; i >= 0; i--) {
          const p = toAppend[i]!;
          if (!seenSet.has(p.id!)) {
            target.push(p);
            seenSet.add(p.id!);
          }
        }
      };

      const missingParentData: T[] = [];
      const addedParentIds = new Set<RowId>();

      for (const child of filteredData) {
        const chain = buildParentChain(child);
        appendUnique(missingParentData, addedParentIds, chain);
      }

      const out: T[] = [];
      const seen = new Set<RowId>();
      for (const item of [...missingParentData, ...filteredData]) {
        if (!seen.has(item.id!)) {
          seen.add(item.id!);
          out.push(item);
        }
      }
      return out;
    };

    let modifiedResponse: T[];
    const filtered = applyFiltersToData(props.dataset || []);

    if (appliedFilters.length) {
      let filteredData = filtered;
      if (props.treeGrid) {
        filteredData = includeMissingParentsForTree(filteredData, dataset);
      }
      modifiedResponse = filteredData;
      setTotalItemCount(filteredData.length);
    } else {
      modifiedResponse = props.dataset || [];
      setTotalItemCount(modifiedResponse.length);
    }

    if (sortingDetails) {
      const col = props.columnDefinitions.find((c) => c.id === sortingDetails.columnId);
      const getter = col?._valueGetter;
      modifiedResponse = [...modifiedResponse].sort((a, b) => {
        const va = getter ? getter(a) : get(a, sortingDetails.columnId);
        const vb = getter ? getter(b) : get(b, sortingDetails.columnId);
        if (va > vb) return sortingDetails.sortAsc ? 1 : -1;
        if (va < vb) return sortingDetails.sortAsc ? -1 : 1;
        return 0;
      });
    }

    return modifiedResponse.slice(skip, take + skip);
  };

  const loadData = async (skip: number, take: number, appendNext = false) => {
    let modifiedResponse: T[] = [];

    if (props.query && props.remoteDataSource) {
      const modifiedQuery = buildModifiedQuery(skip, take);
      // Dynamic remoteDataSource URL - endpoint not known at compile time
      const response = await axiosWrapper.post<RemoteDataResponse>(props.remoteDataSource, { value: modifiedQuery });
      modifiedResponse = processRemoteResponse(response);
    } else if (props.dataset) {
      modifiedResponse = processLocalDataset(skip, take);
    }

    if (props.treeGrid && modifiedResponse.length) {
      modifiedResponse = mapTreeGridFlags(modifiedResponse);
    }

    modifiedResponse = ensureIds(modifiedResponse);

    setLoaderOnPageChange(false);

    if (appendNext) {
      setDataset((prev) => [...prev, ...modifiedResponse]);
      setLoaderForInfiniteScroll(false);
    } else {
      setDataset(modifiedResponse);
    }

    const loadedGrid = gridRef.current?.slickGrid;
    if (loadedGrid) {
      loadedGrid.setSelectedRows([]);
    }

    props.setGridReady?.(true);
  };

  const handleDataLoad = () => {
    if (props.allowPagination) {
      loadData((currentPage - 1) * pageSize, pageSize);
    } else if (props.infiniteScroll) {
      loadData(0, FETCH_SIZE);
    } else {
      loadData(0, NO_OF_FETCHED_ROWS);
    }
  };

  useEffect(() => {
    setLoaderOnPageChange(true);
    const timeout = setTimeout(() => {
      handleDataLoad();
    }, 0);
    return () => clearTimeout(timeout);
  }, [pageSize, currentPage, props.query, props.dataset, sortingDetails, appliedFilters]);

  useEffect(() => {
    if (props.treeGrid && !props.disableTreeCollapseOnDataChange) {
      gridRef.current?.treeDataService.toggleTreeDataCollapse(false);
    }
  }, [dataset, props.treeGrid, props.disableTreeCollapseOnDataChange, gridRef]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    const keysToStop = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End", "v", "c", "x"];
    if (keysToStop.includes(e.code)) {
      e.stopPropagation();
    }
  };

  interface ScrollEventDetail {
    detail: {
      args: OnScrollEventArgs;
    };
  }

  const handleOnScroll = debounce(($event: ScrollEventDetail) => {
    const args = $event.detail.args;
    const viewportElm = args.grid.getViewportNode();
    if (!viewportElm) return;

    if (
      ["mousewheel", "scroll"].includes(args.triggeredBy || "") &&
      viewportElm.scrollTop > 0 &&
      Math.ceil(viewportElm.offsetHeight + args.scrollTop) >= args.scrollHeight
    ) {
      const startIdx = gridRef.current?.dataView.getItemCount() || 0;
      const st = stateRef.current;
      if (st && st.totalItemCount >= st.dataset.length) {
        if (!isLastRef.current) {
          setLoaderForInfiniteScroll(true);
          loadData(startIdx, FETCH_SIZE, true);
        }
      }
    }
  }, 200);

  useEffect(() => {
    const renderEditorTemplate = (updatedColumn: ColumnDefinition<T>, args: T) => {
      const value = updatedColumn._valueGetter
        ? updatedColumn._valueGetter(args)
        : get(args, String(updatedColumn.field));
      const container = document.createElement("div");
      container.classList.add("w-full");
      return mountCellReact(
        <Box className="w-full h-full flex" onKeyDown={handleKeyPress}>
          {updatedColumn.editorTemplate?.({ value, updatedColumn, args })}
        </Box>,
        container,
      );
    };

    const columnDefinitions = props.columnDefinitions.map((column) => {
      let updated: ColumnDefinition<T> = {
        sortable: !props.isEditMode,
        excelExportOptions: {
          valueParserCallback: (_: unknown, { dataContext }: { dataContext: T }) =>
            column._valueGetter ? column._valueGetter(dataContext) : get(dataContext, String(column.field), ""),
        },
        ...column,
      };

      updated.formatter = buildBaseFormatter(column as ColumnDefinition<T>, !!props.isEditMode);

      if (props.isEditMode && updated.editorTemplate) {
        updated.formatter = (
          row: number,
          _col: number,
          _value: unknown,
          _columnDef: unknown,
          args: T,
          grid?: unknown,
        ) => {
          const selectedRow = gridRef.current?.slickGrid.getSelectedRows()[0];
          if (selectedRow === row) {
            return renderEditorTemplate(updated, args);
          }
          if (column.formatter) {
            return column.formatter(row, _col, _value, _columnDef, args, grid);
          }
          if (column._valueGetter) return column._valueGetter(args);
          return get(args, String(column.field));
        };
      }

      if (column.filterType && !props.isEditMode) {
        const buttoncssClass = appliedFilters.includes(String(column.id))
          ? "mdi mdi-filter-variant text-[var(--primary-gradient-color1)]"
          : "mdi mdi-filter-variant text-slate-900";

        updated = {
          filterable: true,
          sortable: true,
          groupable: true,
          type: "string",
          header: {
            ...column.header,
            buttons: [
              {
                cssClass: buttoncssClass,
                command: "toggle-highlight",
                tooltip: String(column.id),
              },
            ],
          },
          ...updated,
        };
      }

      return updated;
    });

    if (props.isEditMode) {
      setUpdatedColumnDefinitions(columnDefinitions);
    } else {
      setUpdatedColumnDefinitions([...specialColumnRef.current, ...columnDefinitions]);
    }
  }, [props.columnDefinitions, props.isEditMode, appliedFilters, props.preAppliedFilters]);

  useEffect(() => {
    if (!props.dragAndDrop) return;

    let intervalId: ReturnType<typeof setInterval> | null = null;
    let lastTop: number | null = null;

    const checkPosition = () => {
      const el = document.querySelector(".slick-reorder-proxy");
      if (el) {
        const { top } = el.getBoundingClientRect();
        if (top !== lastTop) {
          topOfDragRowRef.current = top;
          setTop(top);
          lastTop = top;
        }
      }
    };

    const observer = new MutationObserver(() => {
      const element = document.querySelector(".slick-reorder-proxy");
      if (element && !intervalId) {
        intervalId = setInterval(checkPosition, 100);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      if (intervalId) clearInterval(intervalId);
    };
  }, [props.dragAndDrop]);

  useEffect(() => {
    const sg = gridRef.current?.slickGrid;
    if (props.enableWrapText && sg) {
      sg.onColumnsResized.notify({ triggeredByColumn: "", grid: sg });
    }
  }, [dataset, props.isEditMode, props.enableWrapText, gridRef]);

  const handleFilterIconClick = (anchorElement: Element, columnIdArg: string, filterTypeArg: FilterType) => {
    const rect = anchorElement.getBoundingClientRect();
    setFilterOpened(true);
    setAnchorEl({ top: rect.top, left: rect.left });
    setCurrentColumnId(columnIdArg);
    setFilterType(filterTypeArg);
  };

  const onBeforeMoveRows = (_e: Event, data: RowMoveData): boolean => {
    setInsertBefore(data.insertBefore);
    setDataRow(data.rows[0] ?? null);

    const firstRow = data.rows[0];
    if (firstRow !== undefined && firstRow < data.insertBefore) return false;
    for (const r of data.rows) {
      if (r === data.insertBefore - 1) {
        _e.stopPropagation();
        return false;
      }
    }
    return true;
  };

  interface DropTargetOptions {
    currentTop: number | undefined;
    insertBeforeIdx: number;
    movedRowElement: Element | null;
    droppedRowElement: Element | null;
    droppedRowData: T | null;
  }

  const determineDropTarget = (opts: DropTargetOptions): { newParentId: ParentId; newInsertPosition: number } => {
    const { currentTop, insertBeforeIdx, movedRowElement, droppedRowElement, droppedRowData } = opts;
    let newParentId: ParentId = null;
    let newInsertPosition = insertBeforeIdx;

    if (!droppedRowElement) {
      return { newParentId, newInsertPosition };
    }

    const movedRectTop = movedRowElement?.getBoundingClientRect().top ?? -Infinity;
    const rowRect = droppedRowElement.getBoundingClientRect();
    const midpointY = rowRect.top + rowRect.height / 2;

    if (movedRectTop < rowRect.top) {
      return { newParentId, newInsertPosition };
    }

    if (isMidpointHit(currentTop ?? -Infinity, droppedRowElement)) {
      const dropId = droppedRowData?._id;
      newParentId = dropId != null ? (typeof dropId === "object" ? dropId.toString() : dropId) : null;
      newInsertPosition = insertBeforeIdx;
    } else if (
      currentTop !== undefined &&
      currentTop < rowRect.top + rowRect.height &&
      currentTop >= midpointY + DRAG_TOLERANCE_PX
    ) {
      newInsertPosition = insertBeforeIdx;
    }

    return { newParentId, newInsertPosition };
  };

  const reconcileOldParentChildren = (previousParentId: RowId | undefined, treeData: T[]): void => {
    if (!previousParentId) return;
    const oldParent = treeData.find((it) => it._id === previousParentId);
    if (!oldParent) return;
    const hasChildren = treeData.some((it) => it.parentId === oldParent._id);
    if (!hasChildren) {
      oldParent.__hasChildren = false;
      oldParent.hasChild = false;
    }
  };

  const tryApplyRowDrop = async (
    newParentId: ParentId,
    movedRowData: T,
    droppedRowData: T | null,
    previousParentId: RowId | undefined,
    treeData: T[],
  ): Promise<boolean> => {
    setLoaderOnPageChange(true);
    const response = await props.rowDrop?.({ parentId: newParentId ?? null, movedRowData });
    setLoaderOnPageChange(false);
    if (!response) return false;

    movedRowData.parentId = newParentId;
    movedRowData.treeLevel = newParentId ? (droppedRowData?.treeLevel || 0) + 1 : 0;

    if (newParentId && droppedRowData) {
      droppedRowData.__hasChildren = true;
      droppedRowData.hasChild = true;
    }

    reconcileOldParentChildren(previousParentId, treeData);
    return true;
  };

  const onMoveRows = async (_e: Event, args: RowMoveData): Promise<void> => {
    const { rows, insertBefore: insertBeforeIdx } = args;

    if (gridRef.current) {
      gridRef.current.dataView.sort(() => 0, true);
    }

    const currentTop = topOfDragRowRef.current!;
    const row = rows[0];
    if (row == null) return;

    const dv = gridRef.current?.dataView;
    const itemsSource = getItemsSourceSafe(dv);
    const data = [...itemsSource];
    const movedRowData = data[row];
    if (!movedRowData) return;
    const previousParentId = movedRowData.parentId;

    const insertIndex = typeof insertBeforeIdx === "number" ? insertBeforeIdx : null;
    const droppedRowIndex = insertIndex == null || insertIndex === 0 ? null : insertIndex - 1;
    const droppedRowData = droppedRowIndex == null ? null : (data[droppedRowIndex] ?? null);

    const movedRowElement = getRowElementByIndex(row);
    const droppedRowElement = droppedRowIndex == null ? null : getRowElementByIndex(droppedRowIndex);

    const { newParentId, newInsertPosition } = determineDropTarget({
      currentTop,
      insertBeforeIdx,
      movedRowElement,
      droppedRowElement,
      droppedRowData,
    });

    let treeData: T[] = gridRef.current?.dataView.getItems() ?? [];

    if (newParentId || insertBeforeIdx === 0) {
      await tryApplyRowDrop(newParentId ?? null, movedRowData, droppedRowData, previousParentId, treeData);
    }

    treeData = treeData.filter((it) => it._id !== movedRowData._id);
    treeData.splice(newInsertPosition, 0, movedRowData);

    setDataset(treeData);
    gridRef.current?.slickGrid.invalidate();
  };

  const handleDrag = (): void => {
    if (!gridRef.current) return;

    const reorderGuide = document.querySelector<HTMLElement>(".slick-reorder-guide");
    if (!reorderGuide) return;

    const dv = gridRef.current.dataView;
    const itemsSource = getItemsSourceSafe(dv);
    const dataRows = [...itemsSource];

    const currentTop = topOfDragRowRef.current!;
    // No drop position means there is nothing to draw the guide against.
    if (insertBefore === null) return;
    const iBefore = insertBefore;

    if (dataRow == null || dataRow === iBefore - 1) return;

    const droppedRowIndex = getDroppedRowIndex(iBefore);
    if (droppedRowIndex == null) return;

    const rowEls = getRowElementsSafe(dataRow, droppedRowIndex);
    if (!rowEls) return;

    const { movedEl, droppedEl } = rowEls;
    const movedElTop = movedEl.getBoundingClientRect().top;

    const rowRect = droppedEl.getBoundingClientRect();
    const rowRectTop = rowRect.top;

    const droppedRowData = dataRows[droppedRowIndex] ?? null;
    const movedRowData = dataRows[dataRow];

    const hide = shouldHideGuide({
      movedElTop,
      rowRectTop,
      droppedRowData,
      movedRowData,
      currentTop,
      droppedEl,
      insertBefore: iBefore,
    });

    if (hide) {
      setGuide(reorderGuide, false, false);
      return;
    }

    const midpoint = isMidpointHit(currentTop, droppedEl);
    setGuide(reorderGuide, true, midpoint);
  };

  useEffect(() => {
    handleDrag();
  }, [top]);

  useEffect(() => {
    const grid = gridRef.current?.slickGrid;
    const dv = gridRef.current?.dataView;
    if (!grid || !dv) return;

    const KEY = "disabled-rows";

    const applyDisabledStyles = () => {
      grid.removeCellCssStyles(KEY);

      if (!Array.isArray(props.disabledIds) || !props.disabledIds.length) return;

      const cols = grid.getColumns();
      const styles: Record<number, Record<string, string>> = {};

      for (const id of props.disabledIds) {
        const rowIdx = dv.getRowById(String(id));
        if (rowIdx == null || rowIdx < 0) continue;

        styles[rowIdx] = {};
        for (const col of cols) {
          styles[rowIdx][col.id] = "slick-disabled-row";
        }
      }

      grid.setCellCssStyles(KEY, styles);
    };

    applyDisabledStyles();

    const rerun = () => applyDisabledStyles();
    dv.onRowsChanged.subscribe(rerun);
    dv.onRowCountChanged.subscribe(rerun);

    return () => {
      grid.removeCellCssStyles(KEY);
      dv.onRowsChanged.unsubscribe(rerun);
      dv.onRowCountChanged.unsubscribe(rerun);
    };
  }, [props.disabledIds, gridRef]);

  const onSortCallback = useCallback(
    (_e: unknown, args: unknown) => {
      if (!props.disableSorting && args && typeof args === "object") {
        const sortArgs = args as { columnId?: string | number; sortAsc?: boolean };
        const columnId = String(sortArgs.columnId ?? "");
        const sortAsc = Boolean(sortArgs.sortAsc);
        setSortingDetails({ columnId, sortAsc });
      }
    },
    [props.disableSorting],
  );

  const reactGridReady = (reactGrid: SlickgridReactInstance): void => {
    gridRef.current = reactGrid as SlickgridReactInstanceX<T>;

    // The grid registers the very instance handed to it through `gridOptions.externalResources`,
    // so the registered Excel service and this one are the same object.
    setExcelExportExtension(excelExportService);

    setGridMounted(true);
    reactGrid.slickGrid.invalidate();

    // Grid teardown: sweep every live cell/header React root before SlickGrid destroys the DOM.
    reactGrid.slickGrid.onBeforeDestroy.subscribe(() => {
      unmountAllCellRoots(reactGrid.slickGrid.getContainerNode());
    });

    if (!props.disableCustomSorting) {
      reactGrid.slickGrid.onSort.subscribe(onSortCallback);
    }

    specialColumnRef.current = reactGrid.slickGrid
      .getColumns()
      .filter((c) => ["_checkbox_selector", "_move"].includes(String(c.id)))
      .map((c: Column<T>) => mapSpecialColumn(c));

    if (props.enableWrapText) {
      setTimeout(() => {
        handleWrapUpdate();
      }, INITIAL_WRAP_RUN_DELAY_MS);
    }

    if (props.treeGrid) {
      reactGrid.filterService.disableFilterFunctionality();
    }
    reactGrid.slickGrid.onColumnsReordered.subscribe((e, args) => {
      // `getColumns()`, not `getVisibleColumns()`: slickgrid 10 reinserts hidden columns at their
      // original indices before firing this event (core/slickGrid.js:1569-1585), so re-`setColumns()`
      // with only the visible ones would drop every hidden column out of the grid for good.
      const cols: Column<T>[] = gridRef.current?.slickGrid.getColumns() ?? [];
      const corrected = [
        cols.find((c) => c.id === "_checkbox_selector"),
        cols.find((c) => c.id === "_move"),
        ...cols.filter((c) => !["_checkbox_selector", "_move"].includes(String(c.id))),
      ].filter((c) => c !== undefined);

      gridRef.current?.slickGrid.setColumns(corrected);

      if (props.onColumnsReordered) {
        props.onColumnsReordered(e, args);
      }
    });

    props.onReactGridCreated?.(reactGrid);
  };

  // An alias rather than an interface, so it carries the implicit index signature that lets it be
  // handed to the `SlickEventArgs` bag the public prop is declared with.
  type SelectedRowIdsChangedEvent = {
    detail: {
      args: {
        selectedRowIds: RowId[];
      };
    };
  };

  const onSelectedRowIdsChanged = (args: SelectedRowIdsChangedEvent): void => {
    setSelectedItems(args.detail.args.selectedRowIds.length);
    props.onSelectedRowIdsChanged?.(args);
  };

  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.appliedFilters = appliedFilters;
      if (props.onFilterChange && appliedFilters.length > 0) {
        props.onFilterChange(appliedFilters, filterConditions);
      }
    }
  }, [appliedFilters, filterConditions, props, gridRef]);

  useEffect(() => {
    if (props.preAppliedFilters && Object.keys(props.preAppliedFilters).length > 0) {
      setAppliedFilters(Object.keys(props.preAppliedFilters));
      setFilterConditions(props.preAppliedFilters);
    }
  }, [props.preAppliedFilters]);

  useEffect(() => {
    const current = gridRef.current;
    if (current?.deleteRow) {
      current.deleteRow = false;
      current.slickGrid.setSelectedRows([]);
    }
  }, [gridRef]);

  // Takes the full `RowId`: an id can be an ObjectId-like object, and it is forwarded verbatim as
  // the predicate value the backend matches on.
  const getChildData = async (idParam: RowId): Promise<T[]> => {
    const modifiedQuery: QueryShape = { ...props.query };
    const predicate: QueryPredicate = {
      field: String(props.parentIdMapping),
      operator: "equal",
      value: idParam,
      isComplex: false,
      ignoreCase: true,
      ignoreAccent: false,
    };
    modifiedQuery.where = [predicate];
    // Dynamic remoteDataSource URL - endpoint not known at compile time
    // Widened on assignment: axios declares `AxiosResponse.data` as always present, but it holds
    // whatever the endpoint actually sent - an empty or `null` JSON body arrives with no usable
    // `data` at all. `processRemoteResponse` types the very same axiosWrapper result the same way
    // (:1365), so both readers of this endpoint agree about what can be missing.
    const response: { data?: RemoteDataResponse } = await axiosWrapper.post<RemoteDataResponse>(
      props.remoteDataSource!,
      { value: modifiedQuery },
    );
    const arr: T[] = props.beforeDataBound ? props.beforeDataBound(response) : (response.data?.result ?? []);
    return arr.map((d) => {
      if (!d.id) d.id = d._id;
      return d;
    });
  };

  useEffect(() => {
    if (gridRef.current && gridMounted && props.surpassedDataset?.length) {
      gridRef.current.dataView.setItems(props.surpassedDataset);
      gridRef.current.slickGrid.invalidate();
    }
  }, [props.surpassedDataset, gridMounted, gridRef]);

  const handleRowClickSelection = (rowIndex: number): void => {
    const slickGrid = gridRef.current?.slickGrid;
    const selRows: number[] = slickGrid?.getSelectedRows() ?? [];
    if (selRows.includes(rowIndex)) {
      slickGrid?.setSelectedRows(selRows.filter((row) => row !== rowIndex));
      return;
    }
    const isMulti = Boolean(gridOptions.multiSelect ?? true);
    const next = isMulti ? [...selRows, rowIndex] : [rowIndex];
    slickGrid?.setSelectedRows(next);
  };

  const handleTreeGridClick = async (rowIndex: number, cellIndex: number): Promise<void> => {
    if (!props.treeGrid || props.disableChildDataFetch) return;
    if (cellIndex !== props.dropdownColumn) return;

    const { hasChildMapping } = props;
    // Without a mapping there is no "has children" flag to read, so there is nothing to expand.
    if (hasChildMapping === undefined) return;
    const items: T[] = gridRef.current?.dataView.getItems() ?? [];
    const row = items[rowIndex];
    if (!row || !(row as Record<string, unknown>)[hasChildMapping]) return;

    if (!row.isExpanded) {
      setLoaderOnPageChange(true);
      const indentLevel = row.indentLevel ? row.indentLevel + 1 : 1;
      const dv = gridRef.current!.dataView;
      const data = getItemsSourceSafe(dv);

      let childData = await getChildData(row.id!);
      childData = childData.filter((d) => d._id !== row._id);

      const parentIndex = data.findIndex((r) => r.id === row.id);

      const childRows = childData.map((child) => ({
        ...child,
        indentLevel,
        parentId: row.id,
        [hasChildMapping]: (child as Record<string, unknown>)[hasChildMapping] || false,
        __hasChildren: Boolean((child as Record<string, unknown>)[hasChildMapping]),
        treeLevel: indentLevel,
        __collapsed: true,
        isExpanded: false,
      })) as T[];

      const updatedData = [...data.slice(0, parentIndex + 1), ...childRows, ...data.slice(parentIndex + 1)];
      const parentItem = updatedData[parentIndex];
      if (parentItem) {
        updatedData[parentIndex] = { ...parentItem, isExpanded: true };
      }

      setDataset([...updatedData]);
      setLoaderOnPageChange(false);
    }
  };

  const onClick = (e: ClickEventType): void => {
    if (props.selectCheckBoxOnRowClick && !isEditModeRef.current) {
      handleRowClickSelection(e.detail.args.row);
    }
    props.onClick?.(e, {});
    void handleTreeGridClick(e.detail.args.row, e.detail.args.cell);
  };

  const onDblClick = (e: ClickEventType): void => {
    props.onDblClick?.(e);
  };

  const gridOptions: GridOption = React.useMemo(() => {
    const base: GridOption = {
      sanitizer: (dirtyHtml: string) => DOMPurify.sanitize(dirtyHtml),
      enableContextMenu: false,
      enableHeaderButton: true,
      enableGridMenu: props.enableGridMenu ?? false,
      enableColumnPicker: props.enableColumnPicker ?? true,
      showCustomFooter: false,
      enableHeaderMenu: false,
      enableSorting: false,
      enableCellRowSpan: true,
      rowTopOffsetRenderType: "top",
      enableColumnReorder: true,
      enableCellNavigation: !props.isEditMode,
      headerButton: {
        onCommand: (e, args) => {
          // Read the filter type off the LIVE grid column, never off a captured definition:
          // slickgrid-react applies `gridOptions` once at grid init and never re-reads the prop
          // (dist/esm/components/slickgrid-react.js:512-528), so this callback is registered once
          // and anything closed over here goes stale the moment the caller changes its columns.
          // The click listener is bound directly to the filter button `<li>`, which the plugin
          // builds with no children, so `e.target` is that button - what the popup anchors to.
          const filterTypeArg = toFilterType(args.column);
          const anchor = e.target;
          if (!filterTypeArg || !anchor) return;
          handleFilterIconClick(anchor, String(args.column.field), filterTypeArg);
        },
      },
      gridMenu: {
        onCommand: (_e: unknown) => {},
      },
      ...(props.hasChild && {
        preRegisterExternalExtensions: (pubSubService: PubSubService) => {
          // The grid declares this callback with the `BasePubSubService` interface but always hands
          // over its concrete `EventPubSubService`, which is the type ReactRowDetailView's
          // constructor demands (@slickgrid-universal/react-row-detail-plugin/dist/reactRowDetailView.d.ts:25).
          const rowDetail = new ReactRowDetailView(
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- the published .d.ts is wider than the shipped code: @slickgrid-universal/common/dist/interfaces/gridOption.interface.d.ts:449 declares the callback param as `BasePubSubService`, while dist/services/extension.service.js:304 always passes `this.pubSubService`, the concrete `EventPubSubService` that @slickgrid-universal/react-row-detail-plugin/dist/reactRowDetailView.d.ts:25 requires
            pubSubService as ConstructorParameters<typeof ReactRowDetailView>[0],
          );
          return [{ name: ExtensionName.rowDetailView, instance: rowDetail }];
        },
      }),
      externalResources: [excelExportService],
      enableExcelExport: true,
      // CUBE-27860 / Sentry CUBE-FRONTEND-C63. enableExcelCopyBuffer is on for Ctrl+C copy-out to
      // Excel only; nothing here persists a Ctrl+V paste (no grid sets `editable`, editorTemplate
      // cells are React formatters, and no caller wires onPasteCells), so a paste only mutated the
      // in-memory row until the next fetch. It also crashed: slickgrid 5.x reads
      // `columns[activeCell + x].hidden` BEFORE bounds-checking `destx < maxDestX`
      // (@slickgrid-universal/common@5.14.0 slickCellExternalCopyManager.js:284-293), so pasting
      // more columns than remain right of the anchor cell threw "Cannot read properties of
      // undefined (reading 'hidden')". readOnlyMode short-circuits only the Ctrl+V branch and
      // leaves copy intact. Upstream added `if (!column) break;` in 10.5.2 - 5.14.0 is the last
      // published 5.x, so drop this once the slickgrid 10 upgrade lands.
      enableExcelCopyBuffer: true,
      excelCopyBufferOptions: { readOnlyMode: true },
      createPreHeaderPanel: true,
      preHeaderPanelHeight: 28,
      rowHeight: 48,
      multiColumnSort: false,
      multiSelect: props.enableMultiSelect ?? true,
      enableAutoSizeColumns: true,
      // NOTE (React cell roots): SlickGrid's async post-render/cleanup machinery is
      // deliberately DISABLED (library defaults) — its cleanup queue grows unbounded
      // under fast scrolling and each drain tick does O(queue) work, degrading the
      // grid the longer you scroll. cellReactRoot.ts reclaims React roots by
      // DOM-connectivity instead (no grid hooks needed).
      enableAutoResize: true,
      enableFiltering: Object.hasOwn(props, "enableFiltering") ? !!props.enableFiltering : false,
      enableDraggableGrouping: props.allowGrouping ?? false,
      enableSelection: !props.isEditMode,
      createTopHeaderPanel: true,
      topHeaderPanelHeight: 35,
      enableCheckboxSelector: !props.isEditMode,
      checkboxSelector: { toolTip: "" },
      selectionOptions: { selectActiveRow: false },
      ...(props.treeGrid && {
        enableTreeData: true,
        treeDataOptions: {
          columnId: String(props.initialSortColumnId ?? ""),
          parentPropName: String(props.parentIdMapping ?? ""),
          levelPropName: "treeLevel",
          indentMarginLeft: 25,
          initiallyCollapsed: true,
          initialSort: {
            columnId: String(props.initialSortColumnId ?? ""),
            direction: props.initialSortDirection ?? "DESC",
          },
          titleFormatter: props.titleFormatter,
        },
      }),
      ...(props.dragAndDrop && {
        enableRowMoveManager: true,
        rowMoveManager: {
          columnIndexPosition: 0,
          cancelEditOnDrag: true,
          disableRowSelection: true,
          hideRowMoveShadow: false,
          onBeforeMoveRows,
          onMoveRows: (e: Event, args: RowMoveData) => {
            void onMoveRows(e, args);
          },
        },
      }),
      autoResize: {
        container: `#${id.replaceAll(":", "_")}`,
        resizeDetection: "container",
        calculateAvailableSizeBy: "container",
      },
      autoHeight: !!props.hasChild,
      ...props.gridOptions,
    };

    if (props.allowGrouping) {
      base.draggableGrouping = {
        dropPlaceHolderText: "Drop a column header here to group by the column",
        deleteIconCssClass: "mdi mdi-close color-danger",
        sortAscIconCssClass: "mdi mdi-arrow-up",
        sortDescIconCssClass: "mdi mdi-arrow-down",
        onGroupChanged: props.onGroupChanged,
        onExtensionRegistered: props.onExtensionRegistered,
      };
    }

    return base;
  }, [excelExportService, id, props]);

  /**
   * ToolBar's column chooser has a column shape of its own - `{ id: string; name?: string |
   * HTMLElement; hidden?; alwaysHidden? }` (ToolBar.tsx:39-44) - and it is neither this file's
   * `ColumnDefinition` nor the vendor `Column`: its `id` is a plain string where both of those
   * allow a number, and its `name` has no `DocumentFragment` arm where the vendor's does. So the
   * three fields it actually reads are projected onto it, converting each one, rather than
   * asserting one shape onto the other. A `DocumentFragment` name is flattened to its text because
   * that is exactly what ToolBar renders for a non-string name (`col.name?.textContent || ""`).
   * `alwaysHidden` is ToolBar's own flag, set by nothing in the tree, so it is left unset.
   */
  const toolBarColumns: ToolBarColumn[] = React.useMemo(
    () =>
      updatedColumnDefinitions.map((column) => ({
        id: String(column.id),
        name: column.name instanceof DocumentFragment ? column.name.textContent : column.name,
        hidden: column.hidden,
      })),
    [updatedColumnDefinitions],
  );

  /**
   * ToolBar hands a projected column back, so the caller's own definition is looked up by id before
   * the callback runs: `callbackForVisibleColumnsUpdate` promises a `ValidatedColumnDefinition<T>`
   * and `props.columnDefinitions` is the only place one exists. The grid's own special columns
   * (`_checkbox_selector`, `_move`) match nothing there, which is also all a caller could do with
   * them - they key their update off their own column ids.
   */
  const handleVisibleColumnsUpdate = (columns: ToolBarColumn[]): void => {
    const callback = props.callbackForVisibleColumnsUpdate;
    if (!callback) return;
    const updatedColumns = columns
      .map((column) => props.columnDefinitions.find((definition) => String(definition.id) === column.id))
      .filter((definition) => definition !== undefined);
    if (updatedColumns.length) callback(updatedColumns);
  };

  return (
    <div dir="ltr" className="h-full min-w-full cube-slick-grid">
      {!allPrerequisiteLoaded && (
        <div className="w-full pt-4 bg-white">
          <Shimmer />
        </div>
      )}

      {allPrerequisiteLoaded && (
        <div className="flex flex-col h-full min-w-full">
          {props.toolbar && (
            <ToolBar
              toolbar={props.toolbar}
              toolbarClickHandler={props.toolbarClickHandler}
              showToolbarBorder={props.showToolbarBorder}
              enableColumnChooser={props.enableColumnChooser}
              columnDefinitions={toolBarColumns}
              callbackForVisibleColumnsUpdate={handleVisibleColumnsUpdate}
              ref={gridRef}
              height={
                typeof props.toolbarHeight === "string" ? Number.parseFloat(props.toolbarHeight) : props.toolbarHeight
              }
              propertiesPanel={props.propertiesPanel}
              excelExportExtension={excelExportExtension}
              excelExport={!!props.excelExport}
            />
          )}

          <div
            data-testid="slickGrid"
            id={id.replaceAll(":", "_")}
            className={"relative flex h-full min-w-full overflow-auto" + (cssClasses ? " " + cssClasses : "")}
          >
            {/* SlickgridReact library types differ from our strict props - cast at library boundary */}
            {React.createElement(
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- vendored .d.ts is narrower than the shipped code: slickgrid-react/dist/types/components/slickgridReactProps.d.ts:5-6 declares `containerService`/`translaterService` required, while dist/esm/components/slickgrid-react.js:1365 supplies both through `defaultProps`; the same interface also fixes `columnDefinitions: Column[]` non-generically, so the forwarded prop bag cannot be expressed in it
              SlickgridReact as typeof SlickgridReact & React.ComponentType<Record<string, unknown>>,
              {
                ...props,
                ...(!props.datasetHierarchical && !props.surpassedDataset ? { dataset } : {}),
                gridId: `${id.replaceAll(":", "_")}_grid18`,
                gridOptions,
                onReactGridCreated: ($event: { detail: SlickgridReactInstance }) => {
                  reactGridReady($event.detail);
                  // `_paneHeaderL` / `_paneTopL` are `protected` on the vendor's SlickGrid class and
                  // are reached only to stamp the e2e hooks, so read them reflectively and check
                  // what actually comes back.
                  const paneHeader: unknown = Reflect.get($event.detail.slickGrid, "_paneHeaderL");
                  const paneTop: unknown = Reflect.get($event.detail.slickGrid, "_paneTopL");
                  if (paneHeader instanceof HTMLElement) {
                    paneHeader.setAttribute("data-testid", "SlickGridPaneHeader");
                  }
                  if (paneTop instanceof HTMLElement) {
                    paneTop.setAttribute("data-testid", "SlickGridPaneContent");
                  }
                },
                columnDefinitions: updatedColumnDefinitions,
                setTotalItemCount,
                onClick,
                onDblClick,
                onSelectedRowIdsChanged,
                ...(props.infiniteScroll ? { onScroll: handleOnScroll } : {}),
                onAfterSetColumns: () => {
                  if (gridRef.current) {
                    if (props.handleUpdateCurrentView) {
                      props.handleUpdateCurrentView({
                        columns: gridRef.current.slickGrid.getColumns().map((c) => c.id as string | number),
                      });
                    }
                    gridRef.current.dataView.refresh();
                  }
                },
              },
            )}
            {(loaderOnPageChange || props.showLoader) && (
              <div className="absolute inset-0 z-50 flex items-center justify-center bg-[var(--surface-white)] opacity-70 pointer-events-auto">
                <div className="flex items-center justify-center">
                  <CircularProgress />
                </div>
              </div>
            )}
          </div>

          {showPageCount && (
            <div className="!h-[3.125rem] w-full flex items-center justify-between p-3 border-b sticky bottom-0 bg-white shadow-md">
              {/* No `ref` is forwarded: `PagerComponent` names its ref parameter `_ref` and never
                  reads it (Pagination.tsx:37), so the grid handle it used to be handed was dropped
                  on arrival. */}
              <PagerComponent
                pageSize={pageSize}
                currentPage={currentPage}
                totalItemCount={totalItemCount}
                setCurrentPage={setCurrentPage}
                setPageSize={setPageSize}
                totalPages={totalPages}
                allowPagination={!!props.allowPagination}
                selectedItems={selectedItems}
                currentLoadedCount={currentLoadedCount}
                infiniteScroll={props.infiniteScroll}
              />
            </div>
          )}

          <FilterComponent
            currentColumnId={currentColumnId}
            setCurrentColumnId={setCurrentColumnId}
            setFilterPredicates={setFilterPredicates}
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
            filterType={filterType || undefined}
            filterOpened={filterOpened}
            renderOption={props.renderOption}
            setFilterOpened={setFilterOpened}
            datasetForFetchingOptions={
              appliedFilters.length === 0 || (appliedFilters.length === 1 && appliedFilters[0] === currentColumnId)
                ? (props.dataset ?? [])
                : dataset
            }
            setAppliedFilters={setAppliedFilters}
            appliedFilters={appliedFilters}
            // FilterComponent declares its own `{ field, _valueGetter, header }` column shape and a
            // looser applyFilter signature; asserted here at that single boundary. Both need to be
            // widened in FilterComponent.tsx (its column `field` is a plain `string` and its rows
            // are `Record<string, unknown>`), not narrowed here.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- FilterComponent.tsx:129 takes its own column shape, declared at FilterComponent.tsx:29-34 with `field: string` where this file's `field` is `keyof T`; reconciling it belongs there
            columnDefinitions={props.columnDefinitions as typeof props.columnDefinitions & FilterColumn[]}
            beforeDataBound={props.beforeDataBound}
            localData={!props.remoteDataSource}
            ref={gridRef}
            remoteDataSource={props.remoteDataSource}
            query={props.query}
            filterConditions={filterConditions}
            setFilterConditions={setFilterConditions}
            getUpdatedFilterConditions={getUpdatedFilterConditions}
            applyFilter={
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- FilterComponent.tsx:138-144 types the rows it filters as `FilterRow` (`Record<string, unknown>`, line 24) and the option as `string`, neither of which this generic grid's `applyFilterLocal` accepts; reconciling it belongs there
              applyFilterLocal as typeof applyFilterLocal & FilterComponentProps["applyFilter"]
            }
            preAppliedFilters={props.preAppliedFilters}
            onFilterConditionsChange={props.onFilterChange}
          />
        </div>
      )}

      {loaderForInfiniteScroll && (
        <output
          aria-live="polite"
          className="
            absolute bottom-10 start-1/2 -translate-x-1/2
            inline-flex items-center justify-center gap-2
            rounded-full bg-white/90 px-4 py-2
            text-lg font-medium text-slate-800
            shadow-lg shadow-black/5 ring-1 ring-slate-900/5 backdrop-blur-sm
            dark:bg-slate-800/90 dark:text-slate-200 dark:ring-white/10
          "
        >
          <CircularProgress size={18} thickness={5} sx={{ color: "primary.main" }} />
          <span className="text-xs font-semibold text-slate-800 dark:text-slate-100">
            <Text>{t("Loading more…")}</Text>
          </span>
        </output>
      )}
    </div>
  );
  // `forwardRef` cannot carry the render function's own generic parameter, so the generic is erased
  // through the call and re-stated as an assertion on the result — the same shape the public
  // `SlickGrid` wrapper below asserts, so the two agree and the inner component stays checked.
}) as <T extends GridRowBase>(
  props: SlickGridProps<T> & { ref?: React.Ref<SlickgridReactInstanceX<T>> },
) => React.ReactElement;

/**
 * Type-safe SlickGrid component.
 *
 * IMPORTANT: Use `typedColumns<YourRowType>()` to create column definitions
 * for proper type safety.
 *
 * @example
 * interface MyRow extends GridRowBase {
 *   name: string;
 *   count: number;
 * }
 *
 * const columns = typedColumns<MyRow>([
 *   { id: "name", field: "name", name: "Name" },
 *   { id: "count", field: "count", name: "Count" }
 * ]);
 *
 * <SlickGrid
 *   dataset={myData}
 *   columnDefinitions={columns}
 * />
 */
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- same `forwardRef` generic erasure as `SlickGridInner` above: the call returns `ForwardRefExoticComponent<SlickGridProps<GridRowBase> & ...>`, so `T` can only be restored by re-stating the signature; it matches the inner component's own
const SlickGrid = forwardRef(function SlickGrid<T extends GridRowBase>(
  props: Omit<SlickGridProps<T>, "ref">,
  ref: React.Ref<SlickgridReactInstanceX<T>>,
) {
  return <SlickGridInner {...props} ref={ref} />;
}) as <T extends GridRowBase>(
  props: SlickGridProps<T> & { ref?: React.Ref<SlickgridReactInstanceX<T>> },
) => React.ReactElement;

export default SlickGrid;
